import { useState } from "react";
import { API } from "../App";
import { Header, Footer as SiteFooter } from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Search, MapPin, Phone, Mail, Globe, Users, AlertCircle, Loader2 } from "lucide-react";

const COS_LOGO = "https://api.careeronestop.org/api-explorer/assets/img/COS_logo_api.png";

const WorkforceLookupPublic = () => {
  const [zip, setZip] = useState("");
  const [radius, setRadius] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const search = async (e) => {
    e?.preventDefault?.();
    setError("");
    setResult(null);
    if (!/^\d{5}$/.test(zip)) {
      setError("Please enter a valid 5-digit US zipcode.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/public/workforce-board?zip=${zip}&radius=${radius}`);
      const data = await res.json();
      if (!res.ok) {
        setError(data?.detail || "Lookup failed. Please try again.");
      } else if (data.error) {
        setError(data.error);
        setResult(data);
      } else {
        setResult(data);
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Standard site nav */}
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0F1B3D] via-[#1E3A8A] to-[#0A1A4A] text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#FFD507]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-4xl mx-auto px-6 py-14 relative">
          <p className="text-[#FFD507] text-xs uppercase tracking-[0.25em] font-bold mb-3">Free Public Tool</p>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold leading-tight">
            Find your local Workforce Development Board
          </h1>
          <p className="text-slate-300 mt-4 max-w-2xl text-base">
            Enter a US zipcode to discover the Workforce Development Board (WDB) serving your area. WDBs help fund training,
            connect you to American Job Centers, and may pay for tuition through WIOA.
          </p>

          <form onSubmit={search} className="mt-8 bg-white rounded-2xl shadow-2xl p-4 sm:p-6 grid sm:grid-cols-[1fr_140px_auto] gap-3" data-testid="lookup-form">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={zip}
                onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                placeholder="Enter 5-digit zipcode"
                className="pl-10 h-12 text-[#0F1B3D] text-base"
                data-testid="zip-input"
                inputMode="numeric"
                maxLength={5}
              />
            </div>
            <select
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              className="h-12 px-3 rounded-md border border-slate-200 text-slate-700 bg-white focus:outline-none focus:border-[#1E3A8A]"
              data-testid="radius-select"
            >
              {[10, 25, 50, 75, 100].map((r) => (
                <option key={r} value={r}>{r} mi</option>
              ))}
            </select>
            <Button
              type="submit"
              disabled={loading}
              className="h-12 px-6 bg-[#FFD507] hover:bg-[#FFC500] text-[#0F1B3D] font-bold"
              data-testid="search-btn"
            >
              {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Searching</>) : "Search"}
            </Button>
          </form>
        </div>
      </section>

      {/* Results */}
      <section className="max-w-4xl mx-auto px-6 py-10">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="error-message">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              <p className="font-semibold text-red-900">{error}</p>
              <p className="text-sm text-red-700 mt-1">Try a different zipcode or widen the radius.</p>
            </div>
          </div>
        )}

        {result && !error && (
          <div className="mb-6 flex items-center justify-between flex-wrap gap-2" data-testid="result-summary">
            <p className="text-slate-700">
              Found <span className="font-bold text-[#1E3A8A]">{result.count}</span> board{result.count === 1 ? "" : "s"} within{" "}
              <span className="font-semibold">{result.radius} miles</span> of <span className="font-mono font-semibold">{result.zip}</span>
              {result.state && <> in <span className="font-semibold">{result.state}</span></>}.
            </p>
          </div>
        )}

        <div className="space-y-4">
          {(result?.boards || []).map((b) => (
            <Card key={b.id} className="border border-slate-200 hover:border-[#1E3A8A]/40 transition" data-testid={`board-card-${b.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs uppercase tracking-wider font-bold text-[#1E3A8A] bg-[#FFD507]/20 px-2 py-0.5 rounded">
                        {b.level} · {b.type === "WDB" ? "Workforce Development Board" : b.type}
                      </span>
                      {typeof b.distance === "number" && (
                        <span className="text-xs text-slate-500">{b.distance.toFixed(1)} mi away</span>
                      )}
                    </div>
                    <h3 className="text-xl font-heading font-bold text-[#0F1B3D] leading-tight">{b.name}</h3>
                    {b.company && b.company !== b.name && <p className="text-sm text-slate-500 mt-0.5">{b.company}</p>}

                    <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm">
                      {(b.address1 || b.city) && (
                        <div className="flex items-start gap-2 text-slate-700">
                          <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                          <div>
                            {b.address1 && <div>{b.address1}</div>}
                            {b.address2 && <div>{b.address2}</div>}
                            <div>{[b.city, b.state, b.zip].filter(Boolean).join(", ")}</div>
                          </div>
                        </div>
                      )}
                      {b.url && (
                        <a href={b.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 text-[#1E3A8A] hover:underline" data-testid={`board-url-${b.id}`}>
                          <Globe className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span className="break-all">{b.url}</span>
                        </a>
                      )}
                    </div>

                    {b.contacts && b.contacts.length > 0 && (
                      <details className="mt-4 group">
                        <summary className="cursor-pointer text-sm font-semibold text-[#1E3A8A] hover:underline flex items-center gap-1">
                          <Users className="w-4 h-4" /> {b.contacts.length} contact{b.contacts.length === 1 ? "" : "s"}
                        </summary>
                        <div className="mt-3 space-y-3 border-t border-slate-100 pt-3">
                          {b.contacts.slice(0, 5).map((c, idx) => (
                            <div key={idx} className="text-sm bg-slate-50 rounded-lg p-3" data-testid={`board-contact-${b.id}-${idx}`}>
                              <div className="font-semibold text-slate-900">{c.name || "—"}</div>
                              {c.title && <div className="text-xs text-slate-500">{c.title}</div>}
                              {c.group && <div className="text-xs text-slate-400 mt-0.5">{c.group}</div>}
                              <div className="flex flex-wrap gap-3 mt-2 text-xs">
                                {c.email && (
                                  <a href={`mailto:${c.email}`} className="text-[#1E3A8A] hover:underline flex items-center gap-1">
                                    <Mail className="w-3 h-3" /> {c.email}
                                  </a>
                                )}
                                {c.phone && (
                                  <a href={`tel:${c.phone}`} className="text-[#1E3A8A] hover:underline flex items-center gap-1">
                                    <Phone className="w-3 h-3" /> {c.phone}
                                  </a>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {result && result.count === 0 && !error && (
            <Card className="border-dashed border-2 border-slate-200">
              <CardContent className="p-10 text-center" data-testid="empty-state">
                <Search className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="font-semibold text-slate-700">No boards found for that zipcode.</p>
                <p className="text-sm text-slate-500 mt-1">Try widening the search radius or check the zip is correct.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* CareerOneStop attribution - required by their API agreement */}
      <div className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-sm text-slate-600">
            <span>Data provided by</span>
            <a
              href="https://www.careeronestop.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:opacity-80"
              data-testid="careeronestop-attribution"
            >
              <img src={COS_LOGO} alt="CareerOneStop — Your source for career exploration, training & jobs" className="h-10" />
            </a>
          </div>
          <p className="text-xs text-slate-500 text-center sm:text-right">
            CareerOneStop is sponsored by the U.S. Department of Labor.<br />
            Aspiritech Academy is a grant-funded training program.
          </p>
        </div>
      </div>

      {/* Standard site footer */}
      <SiteFooter />
    </div>
  );
};

export default WorkforceLookupPublic;
